//Node Modules
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

//GraphQL
import { API, graphqlOperation } from "aws-amplify";
import { userDashboardConfigBySchemeId } from "../../graphql/queries";

//BinaryForge Components
import { PageHeader } from "../../components/layout";
import {
	AnalyticsLoader,
	AnalyticsSelector,
	DeviceSummaryLoader,
	EventSummaryLoader,
	UserSummaryLoader,
} from "../../components/dashboard";
import HealthTiles from "../../components/dashboard/HealthTiles";
import {
	EventTrendChart,
	RssiDistributionChart,
	BatteryDistributionChart,
} from "../../components/dashboard/DashboardCharts";
import ActivityFeed from "../../components/dashboard/ActivityFeed";
import PinnedReportChart from "../../components/dashboard/PinnedReportChart";
import EstateStatusCard from "../../components/dashboard/EstateStatusCard";
import UserSummaryCard from "../../components/dashboard/UserSummaryCard";

//3rd Party Components
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";

//Atoms
import { dialogAtomFamily, loggedInUserAtom } from "../../atoms";
import { analyticsParamsAtom, pinnedReportsAtom } from "../../atoms/dashboard";
import { selectedUserSchemeAtom } from "../../atoms/user";
import { Note } from "../../components/helpers";

//Helpers
import { parsePinnedReports, useSavePinnedReports } from "../../helpers/Dashboard";

const Home = () => {
	const { t } = useTranslation();

	//Recoil
	const loggedInUser = useRecoilValue(loggedInUserAtom);
	const selectedScheme = useRecoilValue(selectedUserSchemeAtom);
	const [analyticsParams, setAnalyticsParams] = useRecoilState(analyticsParamsAtom);
	const [pinnedReports, setPinnedReports] = useRecoilState(pinnedReportsAtom);
	const setShowAnalyticsSelector = useSetRecoilState(dialogAtomFamily("analyticsSelectorDialog"));
	const savePinnedReports = useSavePinnedReports();

	//Temporary preview toggle for the reworked dashboard - remove once accepted (see #56)
	const [newDashboard, setNewDashboard] = useState(() => localStorage.getItem("andelDashboardNew") !== "false");
	const handleToggleNewDashboard = (value) => {
		setNewDashboard(value);
		localStorage.setItem("andelDashboardNew", String(value));
	};

	useEffect(() => {
		initData();
	}, [selectedScheme]);

	const initData = async () => {
		const {
			data: {
				userDashboardConfigBySchemeId: { items: userDashboardConfig },
			},
		} = await API.graphql(
			graphqlOperation(userDashboardConfigBySchemeId, {
				cognitoUserId: { eq: loggedInUser.sub },
				schemeId: selectedScheme.id,
			})
		);

		if (userDashboardConfig.length > 0) {
			const pins = parsePinnedReports(userDashboardConfig[0].dashboardAnalytics);
			setPinnedReports(pins);
			//Classic single-chart layout reads the first pin
			if (pins.length > 0) setAnalyticsParams(pins[0]);
		}
	};

	const handleUnpinReport = (report) => {
		const pinKey = (pin) => pin.reportId ?? pin.name;
		savePinnedReports(pinnedReports.filter((pin) => pinKey(pin) !== pinKey(report)));
	};

	return (
		<>
			<PageHeader title={t("dashboard.pageTitle")} />
			<main className="appMain">
				<div className="appMain-content">
					{newDashboard && <HealthTiles />}
					{newDashboard && (
						<div className="grid columns-2 gap-large">
							<section className="card">
								<header className="marginBottom-medium">
									<h3>{t("dashboard.charts.eventTrend")}</h3>
								</header>
								<EventTrendChart />
							</section>
							<section className="card">
								<header className="marginBottom-medium">
									<h3>{t("dashboard.activity.header")}</h3>
								</header>
								<ActivityFeed />
							</section>
							<section className="card">
								<header className="marginBottom-medium">
									<h3>{t("dashboard.charts.rssi")}</h3>
								</header>
								<RssiDistributionChart />
							</section>
							<section className="card">
								<header className="marginBottom-medium">
									<h3>{t("dashboard.charts.battery")}</h3>
								</header>
								<BatteryDistributionChart />
							</section>
						</div>
					)}
					<div className="grid columns-3 gap-large">
						{newDashboard ? (
							<>
								<section className="card">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.device.typesHeader")}</h3>
									</header>
									<DeviceSummaryLoader />
									<div id="deviceSummaryLegend" className="marginTop-small"></div>
								</section>
								<section className="card">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.estate.header")}</h3>
									</header>
									<EstateStatusCard />
								</section>
								<section className="card">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.user.usersHeader")}</h3>
									</header>
									<UserSummaryCard />
								</section>
							</>
						) : (
							<>
								<section className="card">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.device.header")}</h3>
									</header>
									<DeviceSummaryLoader />
									<div id="deviceSummaryLegend" className="marginTop-small"></div>
								</section>
								<section className="card">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.event.header")}</h3>
									</header>
									<EventSummaryLoader />
									<div id="eventSummaryLegend" className="marginTop-small"></div>
								</section>
								<section className="card">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.user.header")}</h3>
									</header>
									<UserSummaryLoader />
									<div id="userSummaryLegend" className="marginTop-small"></div>
								</section>
							</>
						)}
						{newDashboard ? (
							<section className="card span-full">
								<div className="flex jContent-spaceBetween">
									<header className="marginBottom-medium">
										<h3>{t("dashboard.analytics.pin.header")}</h3>
									</header>
									<Button
										label={t("dashboard.analytics.pin.add")}
										onClick={() => setShowAnalyticsSelector(true)}
										className="aSelf-start noStyle fontSize-small fontColour-light"
									/>
								</div>
								{pinnedReports.length > 0 ? (
									<div className="grid columns-2 gap-large">
										{pinnedReports.map((report) => (
											<div key={report.reportId ?? report.name} className="flexVert">
												<div className="flex jContent-spaceBetween aItems-center marginBottom-small">
													<h4>{report.name}</h4>
													<Button
														icon="pi pi-times"
														className="noStyle fontColour-light"
														onClick={() => handleUnpinReport(report)}
														aria-label={t("dashboard.analytics.pin.remove")}
														tooltip={t("dashboard.analytics.pin.remove")}
													/>
												</div>
												<PinnedReportChart report={report} />
											</div>
										))}
									</div>
								) : (
									<div className="flexVert aItems-center">
										<Note
											messageKey={t("dashboard.analytics.pin.none")}
											icon="pi pi-info-circle fontColour-info"
											wrapperStyle="aItems-center"
										/>
									</div>
								)}
							</section>
						) : (
							<section className="card span-full">
								{analyticsParams ? (
								<>
									<div className="flex jContent-spaceBetween">
										<header className="marginBottom-medium">
											<h3>{analyticsParams?.name}</h3>
										</header>
										<Button
											label={t("dashboard.analytics.default.open")}
											onClick={() => setShowAnalyticsSelector(true)}
											className="aSelf-start noStyle fontSize-small fontColour-light"
										/>
									</div>
									<AnalyticsLoader />
								</>
							) : (
								<div className="flexVert aItems-center">
									<Note
										messageKey={t("dashboard.analytics.noConfig")}
										icon="pi pi-info-circle fontColour-info"
										wrapperStyle="aItems-center"
									/>
									<Button
										label={t("dashboard.analytics.default.set")}
										onClick={() => setShowAnalyticsSelector(true)}
										className="marginTop-small"
									/>
								</div>
							)}
							</section>
						)}
					</div>
					<div className="actions flex jContent-end aItems-center gap-xsmall">
						<label htmlFor="newDashboardToggle" className="fontSize-small fontColour-light">
							{t("dashboard.newToggle")}
						</label>
						<InputSwitch
							inputId="newDashboardToggle"
							checked={newDashboard}
							onChange={(e) => handleToggleNewDashboard(e.value)}
						/>
					</div>
				</div>
			</main>

			<AnalyticsSelector />
		</>
	);
};

export default Home;
